import { User } from 'models/user.model'

/**
 * Check if user have the required roles provided.
 * If user has no roles or no required role to check return false as default value
 * @param user
 * @param requiredRolesToCheck
 * @returns
 */
export function checkUserRoles(
  user?: User,
  requiredRolesToCheck?: string[],
): boolean {
  if (user?.admin || !requiredRolesToCheck?.length) {
    return true
  } else if (requiredRolesToCheck && user?.roles) {
    const roles = requiredRolesToCheck.find(role => {
      return user.roles[role];
    });
    return !!roles
  }
  // Default case, didn't allow menu access
  return false
}

/**
 * Get the relevant statistic roles for the user
 * @param user
 * @returns array of relevant roles the user has
 */
export function getRelevantStatisticRoles(user?: User): string[] {
  if (!user || !user.roles) return []

  const relevantRoles = ['statistic_ssid', 'statistic_coupons', 'statistic_purchases', 'statistic_chromecast', 'statistic_home', 'statistic_customers']
  return relevantRoles.filter(role => user.roles[role])
}

/**
 * Check if user has access to a specific document type.
 * @param {User} user - The user object.
 * @param {string} type - The document type ('audin' or 'center').
 * @returns {boolean} True if user has access, false otherwise.
 */
export function checkDocumentAccess(user: User, type: string): boolean {
  if (user.admin) return true;

  const hasAudinRole = user.roles.documents_audin_audit || user.roles.documents_audin_integration
  const hasCenterRole = user.roles.documents_financial_report
    || user.roles.documents_indicator_report
    || user.roles.documents_other_documents
    || user.roles.documents_support_documentation

  if (type === 'audin') {
    return hasAudinRole
  } else if (type === 'center') {
    return hasCenterRole
  }

  return false
}

/**
 * Get the appropriate navigation URL for documents based on user access.
 * @param {User} user - The user object.
 * @param {string} targetUrl - The target URL.
 * @returns {string} The appropriate navigation URL.
 */
export function getDocumentNavigationUrl(user: User, targetUrl: string): string {
  const type = targetUrl.split('/').pop() || '';
  if (checkDocumentAccess(user, type)) {
    return targetUrl;
  }
  return user.roles.documents_audin_audit || user.roles.documents_audin_integration
    ? DOCUMENT_URLS.AUDIN
    : DOCUMENT_URLS.CENTER;
}

/**
 * Object containing document-related URLs.
 */
export const DOCUMENT_URLS = {
  AUDIN: '/documents/audin',
  CENTER: '/documents/center',
};
